import { useState } from "react"
import { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { addYears } from "date-fns"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import { DateRangePicker } from "@/components/ui/date-range-picker"
import { DateOnly, DateOnlyFormat } from "@/lib/date-only"
import { formatDate, formatDateRange } from "@/lib/format-date"
import { cn } from "@/lib/utils"

type HistoryDateRangeFilterProps = {
  onFilterChange: (from: string, to: string) => void
  initializeFilter: DateRange
}

export const HistoryDateRangeFilter = (props: HistoryDateRangeFilterProps) => {
  const { onFilterChange, initializeFilter, ...rest } = props
  const { t } = useTranslation()

  const [selectedRange, setSelectedRange] = useState<DateRange>(initializeFilter)

  const handleChange = (selectedRange: DateRange | undefined) => {
    setSelectedRange(selectedRange ?? initializeFilter)
    if (selectedRange?.to) {
      onFilterChange(formatDate(selectedRange.from, DateOnlyFormat), formatDate(selectedRange.to, DateOnlyFormat))
    }
  }

  const handleReset = () => {
    setSelectedRange(initializeFilter)
    onFilterChange(formatDate(initializeFilter.from, DateOnlyFormat), formatDate(initializeFilter.to, DateOnlyFormat))
  }

  const handleLast12Months = () => {
    const today = new Date()
    const oneYearAgo = addYears(new Date(), -1)
    const newRange: DateRange = { from: oneYearAgo, to: today }
    setSelectedRange(newRange)
    onFilterChange(formatDate(oneYearAgo, DateOnlyFormat), formatDate(today, DateOnlyFormat))
  }

  const hasFilter =
    !selectedRange.to ||
    new DateOnly(selectedRange.from!).notEquals(new DateOnly(initializeFilter.from!)) ||
    new DateOnly(selectedRange.to).notEquals(new DateOnly(initializeFilter.to!))

  return (
    <div className="flex items-center gap-2">
      <DateRangePicker showFooter={false} value={selectedRange} onChange={handleChange}>
        {({ selectedRange }) => (
          <Chip {...rest} data-state={"checked"} className="h-8">
            <div className={cn("flex items-center gap-0.5", "text-on-primary-surface")}>
              <Check size={20} />
              <div className="flex-1 truncate px-1.5">{formatDateRange(selectedRange)}</div>
              <CaretDown size={20} />
            </div>
          </Chip>
        )}
      </DateRangePicker>
      <Button variant="tertiary" className="h-8" data-testid="history-last-12-months" onClick={handleLast12Months}>
        {t("patient.history.last12Months")}
      </Button>
      <Button
        variant="tertiary"
        data-testid="history-clear-filter"
        className={cn("h-8", {
          "text-on-surface-disabled pointer-events-none": !hasFilter,
        })}
        onClick={handleReset}>
        {t("common.button.clearFilters")}
      </Button>
    </div>
  )
}
